import "./index.css";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Card, Container, Form } from "react-bootstrap";
import { Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { goBack } from "../../../utils/otherHelopers";
import { ArrowLeftIcon } from "../../../assets/icons/index";
import InputField from "../../../components/form/InputField";
import CustomSelect from "../../../components/form/CustomSelect";
import CommonButton from "../../../components/commonButton";
import { FormFeedback, FormLabel } from "../../../components/form/utils";
import { routes } from "../../../routes/pageRoutes";
import BannerDropzone from "../../../components/BannerDropzone";
import { useDispatch } from "react-redux";
import { addBanner, updateBanner } from "../../../redux/slice/bannerSlice";
import { addBannerSchema } from "../../../schema/validationSchemas";
import { useToast } from "../../../context/TostContext";

const AddBanner = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingImageMeta, setExistingImageMeta] = useState(null);
  const [preview, setPreview] = useState(null);
  const location = useLocation();
  const toast = useToast();
  const { item, isEdit, isView } = location.state || {};

  const headerPortalTarget = document.getElementById("header-portal-target");

  const renderHeaderContent = () => (
    <div className="d-flex align-items-center justify-content-between w-100 pe-3">
      <div className="d-flex align-items-center gap-2">
        <button
          onClick={() => goBack(navigate, location, routes.banner)}
          type="button"
          className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
        >
          <ArrowLeftIcon size={22} />
        </button>
        <h4 className="fw-semibold mb-0 text-primary fs-lg">
          {isView ? "View Banner" : isEdit ? "Update Banner" : "Add Banner"}
        </h4>
      </div>
    </div>
  );
  const handleFileSelect = (e, file, setFieldTouched, values, setValues) => {
    e.stopPropagation();

    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setExistingImageMeta(null); // 👈 important

      setValues({
        ...values,
        image: file,
        hasExistingImage: false,
      });
    } else {
      setSelectedFile(null);
      setPreview(null);
      setExistingImageMeta(null);

      setValues({
        ...values,
        image: null,
        hasExistingImage: false,
      });
    }

    setFieldTouched("image", true, false);
  };

  const initialValues = {
    title: item?.title || "",
    context: item?.context || "",
    placement_key: item?.placement_key || "",
    status: item?.is_active ?? true,
    action_type: item?.action_type && item.action_type !== "none" ? item.action_type : "",
    action_value: item?.action_value || "",
    image: null,
    hasExistingImage: Boolean(isEdit && item?.image_url),
  };

  const contextOptions = [
    { value: "feed", label: "Feed" },
    { value: "tpdp", label: "TPDP" },
    { value: "jantri", label: "Jantri" },
    { value: "marketplace", label: "Marketplace" },
    { value: "profile", label: "Profile" },
    { value: "property", label: "Property" },
    { value: "land", label: "Land" },
    { value: "loan_calculator", label: "Loan Calculator" },
    { value: "unit_conveter", label: "Unit Converter" },
  ];

  const actionTypeOptions = [
    { value: "", label: "None" },
    { value: "url", label: "URL" },
  ];

  const placementOptions = [
    { value: "top", label: "Top" },
    { value: "middle", label: "Middle" },
    { value: "bottom", label: "Bottom" },
  ];

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();

      formData.append("title", values.title);
      formData.append("context", values.context);
      formData.append("placement_key", values.placement_key);
      formData.append("display_mode", "full_width");
      formData.append("status", values.status);
      formData.append("action_type", values.action_type || "none");
      formData.append("action_value", values.action_value || "");

      // only send image if user changed it
      if (selectedFile) {
        formData.append("image", selectedFile);
      }

      let result;

      if (isEdit) {
        result = await dispatch(
          updateBanner({ id: item.id, formData })
        ).unwrap();
      } else {
        result = await dispatch(addBanner(formData)).unwrap();
      }

      if (result?.success) {
        toast.success(result.message || "Banner saved successfully!");
        navigate(routes.banner);
      }


    } catch (err) {
      toast.error(err?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEdit && item?.image_url) {
      setPreview(item.image_url);
      setSelectedFile(null);
      setExistingImageMeta(item.image_meta || null);
    }
  }, [isEdit, item]);

  return (
    <>
      {headerPortalTarget && createPortal(renderHeaderContent(), headerPortalTarget)}
      <Container>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={addBannerSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            handleSubmit,
            isSubmitting,
            setFieldTouched,
            setValues,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Card className="bg-white border-0 p-4 rounded-2 shadow-sm">
                {/* TAB TITLE */}
                <Card.Header className="mb-3 fs-lg bg-transparent p-0 border-0">
                  Banner Details
                </Card.Header>

                {/* FORM GRID */}
                <div className="grid gap-4 ">
                  {/* Banner Name */}
                  <InputField
                    className="g-col-6 g-col-xxl-4 mb-2"
                    label={{ name: "Banner Name" }}
                    input={{
                      id: "title",
                      name: "title",
                      type: "text",
                      placeholder: "Enter Banner Name",
                      value: values.title,
                      onChange: handleChange,
                      autoFocus: !isView,
                      disabled: !!isView,
                    }}
                    validation={{
                      isInvalid: touched.title && !!errors.title,
                      message: errors.title,
                    }}
                  />

                  <CustomSelect
                    label={{ name: "Context" }}
                    name="context"
                    placeholder="Select Context"
                    value={values.context}
                    onChange={(opt) => setFieldValue("context", opt ? opt.value : "")}
                    options={contextOptions}
                    validation={{
                      isInvalid: touched.context && errors.context,
                      message: errors.context,
                    }}
                    isDisabled={!!isView}
                    className="g-col-6 g-col-xxl-4 mb-2"
                  />

                  {/* Placement Dropdown */}
                  <CustomSelect
                    label={{ name: "Placement" }}
                    name="placement_key"
                    placeholder="Select Placement"
                    value={values.placement_key}
                    onChange={(opt) => setFieldValue("placement_key", opt ? opt.value : "")}
                    options={placementOptions}
                    validation={{
                      isInvalid: touched.placement_key && errors.placement_key,
                      message: errors.placement_key,
                    }}
                    isDisabled={!!isView}
                    className="g-col-6 g-col-xxl-4 mb-2"
                  />

                  <CustomSelect
                    label={{ name: "Action Type" }}
                    name="action_type"
                    placeholder="Select Action Type"
                    value={values.action_type}
                    onChange={(opt) => setFieldValue("action_type", opt ? opt.value : "")}
                    options={actionTypeOptions}
                    validation={{
                      isInvalid: touched.action_type && errors.action_type,
                      message: errors.action_type,
                    }}
                    isDisabled={!!isView}
                    className="g-col-6 g-col-xxl-4 mb-2"
                  />

                  {values.action_type === "url" && (
                    <InputField
                      className="g-col-6 g-col-xxl-4 mb-2"
                      label={{ name: "Action Value" }}
                      input={{
                        id: "action_value",
                        name: "action_value",
                        type: "text",
                        placeholder: "Enter URL",
                        value: values.action_value,
                        onChange: handleChange,
                        disabled: !!isView,
                      }}
                      validation={{
                        isInvalid: touched.action_value && !!errors.action_value,
                        message: errors.action_value,
                      }}
                    />
                  )}

                  <div className="g-col-6 g-col-xxl-4 mb-2">
                    <FormLabel labelName="Status" />

                    <div className="d-flex gap-4 status-radio-group">
                      <Form.Check
                        type="radio"
                        label="Active"
                        checked={values.status === true}
                        onChange={() => setFieldValue("status", true)}
                        disabled={!!isView}
                      />

                      <Form.Check
                        type="radio"
                        label="Inactive"
                        checked={values.status === false}
                        onChange={() => setFieldValue("status", false)}
                        disabled={!!isView}
                        className=".custom-plan-switch"
                      />
                    </div>

                    {touched.status && errors.status && (
                      <FormFeedback message={errors.status} />
                    )}
                  </div>

                  {/* Banner Image Upload */}
                  <div className="g-col-12 mb-2 cursor-pointer">
                    <FormLabel labelName="Banner Image" />

                    <BannerDropzone
                      preview={preview}
                      existingImageMeta={existingImageMeta}
                      selectedFile={selectedFile}
                      onFileSelect={(e, file) =>
                        handleFileSelect(
                          e,
                          file,
                          setFieldTouched,
                          values,
                          setValues
                        )
                      }
                      disabled={!!isView}
                    />
                    {touched.image && errors.image && (
                      <FormFeedback message={errors.image} />
                    )}
                  </div>

                  {/* BUTTONS */}
                  <div className="g-col-12 d-flex justify-content-end gap-3">

                    {!isView && (
                      <CommonButton
                        title="Cancel"
                        variant="outline"
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => navigate(routes.banner)}
                      />
                    )}
                    {!isView && (
                      <CommonButton
                        title="Submit"
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                </div>
              </Card>
            </Form>
          )}
        </Formik>
      </Container>
    </>
  );
};

export default AddBanner;
